// Firestore Collection names (matching iOS Constants.swift)
export const Collections = {
  users: 'users',
  teams: 'teams',
  schedules: 'schedules',
  attendances: 'attendances',
  gameRecords: 'gameRecords',
  iceRinks: 'iceRinks',
  workouts: 'workouts',
  marketItems: 'marketItems',
  playerStats: 'playerStats',
  goalieStats: 'goalieStats',
  mvpVotings: 'mvpVotings',
  guestApplications: 'guestApplications',
  guestRecruitments: 'guestRecruitments',
};

// Sub-collections (under teams/{teamId}/...)
export const SubCollections = {
  members: 'members',
  schedules: 'schedules',
  notices: 'notices',
  posts: 'posts',
  polls: 'polls',
  settings: 'settings',
  monthlyDues: 'monthlyDues',
  media: 'media',
  reviews: 'reviews',
};

// Event types (matching iOS Schedule.EventType — regular_game / practice_game / training_with_game / training / meeting / other)
export const EventTypes = {
  regular_game:        { value: 'regular_game',        label: '정기전',        color: '#F59E0B' },
  practice_game:       { value: 'practice_game',       label: '연습경기',      color: '#EF4444' },
  training_with_game:  { value: 'training_with_game',  label: '훈련+게임',     color: '#8B5CF6' },
  training:            { value: 'training',            label: '훈련',          color: '#5BC4D8' },
  meeting:             { value: 'meeting',             label: '미팅',          color: '#10B981' },
  other:               { value: 'other',               label: '기타',          color: '#6B7280' },

  // ── 레거시 키 (구버전 문서 호환) ──
  regularSession: { value: 'regularSession', label: '정기 훈련', color: '#5BC4D8' },
  game:           { value: 'game',           label: '경기',      color: '#F59E0B' },
  tournament:     { value: 'tournament',     label: '대회',      color: '#EF4444' },
  practice:       { value: 'practice',       label: '연습',      color: '#10B981' },
  social:         { value: 'social',         label: '친목',      color: '#EC4899' },
};

// 일정 상태 (필드 없으면 'open'으로 간주 — 기존 문서 호환)
export const ScheduleStatuses = {
  draft:     { value: 'draft',     label: '초안', color: '#9CA3AF', icon: '✏️', desc: '멤버에게 회색으로 보이며 응답 불가' },
  open:      { value: 'open',      label: '공개', color: '#4ADE80', icon: '●',  desc: 'RSVP·출석 응답 가능' },
  closed:    { value: 'closed',    label: '마감', color: '#FBBF24', icon: '🔒', desc: '보이지만 신규 응답 불가' },
  cancelled: { value: 'cancelled', label: '취소', color: '#F87171', icon: '✕',  desc: '취소됨 (기록은 보존)' },
};

// 한글 조사 처리: 받침 없거나 'ㄹ' 받침이면 '로', 그 외 '으로'
export const withRo = (word) => {
  if (!word) return '';
  const code = word.charCodeAt(word.length - 1);
  if (code < 0xAC00 || code > 0xD7A3) return `${word}로`;
  const jong = (code - 0xAC00) % 28;
  return (jong === 0 || jong === 8) ? `${word}로` : `${word}으로`;
};

export const scheduleStatusOf = (schedule) => {
  const v = schedule?.status;
  return ScheduleStatuses[v] ? v : 'open';
};

// 당번 프리셋 (iOS는 title에 영문 기본값, Android는 type에 코드 → 웹은 둘 다 기록)
export const DutyPresets = {
  puck:      { code: 'puck',      title: 'Puck',      label: '퍽 당번',    icon: '🏒' },
  captain:   { code: 'captain',   title: 'Captain',   label: '주장',       icon: '🅲' },
  carpool:   { code: 'carpool',   title: 'Carpool',   label: '카풀',       icon: '🚗' },
  jersey:    { code: 'jersey',    title: 'Jersey',    label: '유니폼 관리', icon: '👕' },
  referee:   { code: 'referee',   title: 'Referee',   label: '심판',       icon: '🦓' },
  first_aid: { code: 'first_aid', title: 'First Aid', label: '응급처치',   icon: '🩹' },
  score:     { code: 'score',     title: 'Score',     label: '기록원',     icon: '📝' },
};

export const DutyStatuses = {
  unassigned: { value: 'unassigned', label: '미지정', color: '#9CA3AF' },
  accepted:   { value: 'accepted',   label: '확정',   color: '#4ADE80' },
  declined:   { value: 'declined',   label: '거절',   color: '#F87171' },
};

// Attendance statuses (matching iOS AttendanceStatus)
export const AttendanceStatuses = {
  attending: { value: 'attending', label: '참석', color: '#4ADE80' },
  not_attending: { value: 'not_attending', label: '불참', color: '#F87171' },
  late: { value: 'late', label: '지각', color: '#FBBF24' },
  early_leave: { value: 'early_leave', label: '조퇴', color: '#FCD34D' },
  undecided: { value: 'undecided', label: '미정', color: '#9CA3AF' },
};

// RSVP responses (matching iOS RSVPResponse)
export const RSVPResponses = {
  will_attend: { value: 'will_attend', label: '참석 예정', color: '#4ADE80' },
  will_not_attend: { value: 'will_not_attend', label: '불참 예정', color: '#F87171' },
  undecided: { value: 'undecided', label: '미정', color: '#9CA3AF' },
};

// Game record types (matching iOS GameRecordType)
export const GameRecordTypes = {
  goal: { value: 'goal', label: '득점', icon: '🏒' },
  assist: { value: 'assist', label: '어시스트', icon: '👆' },
  save: { value: 'save', label: '세이브', icon: '🧤' },
  pim: { value: 'pim', label: '페널티', icon: '⏱️' },
};

// User roles (matching iOS UserRole)
export const UserRoles = {
  coach: { value: 'coach', label: '감독/코치', icon: '🏟️', color: '#EF4444' },
  admin: { value: 'admin', label: '운영진', icon: '⚙️', color: '#6366F1' },
  member: { value: 'member', label: '멤버', icon: '👤', color: '#6B7280' },
  guest: { value: 'guest', label: '게스트', icon: '🎫', color: '#F97316' },
  parent: { value: 'parent', label: '학부모', icon: '👨‍👧', color: '#14B8A6' },
};

// 포지션 — iOS User.position 은 F/D/G 만 받는다(User.swift:144-147).
// 다른 값이 들어가면 iOS 가 그 사용자 문서 전체를 못 읽어 명단에서 사라진다(W1-09).
export const Positions = {
  F: { value: 'F', label: '포워드', category: 'forward' },
  D: { value: 'D', label: '디펜스', category: 'defense' },
  G: { value: 'G', label: '골리',   category: 'goalie' },
};

// 세부 포지션 — iOS DetailedPosition. users/{uid}.positions 배열에 넣는다.
export const DetailedPositions = {
  LW: { value: 'LW', label: '레프트윙',      category: 'forward', base: 'F' },
  C:  { value: 'C',  label: '센터',          category: 'forward', base: 'F' },
  RW: { value: 'RW', label: '라이트윙',      category: 'forward', base: 'F' },
  LD: { value: 'LD', label: '레프트 디펜스', category: 'defense', base: 'D' },
  RD: { value: 'RD', label: '라이트 디펜스', category: 'defense', base: 'D' },
  G:  { value: 'G',  label: '골리',          category: 'goalie',  base: 'G' },
};

// 세부 포지션 → F/D/G
export const basePositionOf = (p) => DetailedPositions[p]?.base || null;

// 표시용 — 예전 문서에 세부 포지션이 position 칸에 들어 있는 경우까지 덮는다.
export const positionLabel = (position, positions) => {
  const detail = Array.isArray(positions) && positions.length
    ? positions.map(x => DetailedPositions[x]?.label || x).join('·')
    : null;
  const base = Positions[position]?.label || DetailedPositions[position]?.label || position || null;
  if (detail && base && !detail.includes(base)) return `${base} (${detail})`;
  return detail || base || '';
};

// 프로필 공개 범위 기본값 — iOS ProfilePrivacy 와 같은 형식.
// 민감 칸은 users/{uid}/private/profile 에 두고, 이 값으로 공개 범위를 정한다.
export const DefaultProfileVisibility = {
  previousTeams: 'friends',
  awards:        'friends',
  equipment:     'friends',
  birthYear:     'friends',
  height:        'onlyMe',
  weight:        'onlyMe',
  phone:         'onlyMe',
};

// Media types
export const MediaTypes = {
  photo: { value: 'photo', label: '사진', icon: '📷' },
  video: { value: 'video', label: '동영상', icon: '🎬' },
};

export const MediaContexts = {
  game: { value: 'game', label: '경기' },
  training: { value: 'training', label: '훈련' },
  general: { value: 'general', label: '일반' },
  event: { value: 'event', label: '행사' },
};

// Media server
export const MediaServer = {
  baseURL: 'https://media.ionice.hockey',
  maxPhotoSize: 50 * 1024 * 1024,
  maxVideoSize: 2 * 1024 * 1024 * 1024,
};

// Date formatting helpers
export const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric', month: 'long', day: 'numeric', weekday: 'short',
  });
};

export const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
};

export const formatDateTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric', month: 'long', day: 'numeric',
    weekday: 'short', hour: '2-digit', minute: '2-digit',
  });
};

export const formatCurrency = (amount) => {
  if (amount == null) return '-';
  return `${amount.toLocaleString()}원`;
};

// "오늘" 판정: 일정의 시작 날짜 또는 종료 날짜가 현재 "하키 날짜"와 같으면 오늘
// 하키 날짜: 새벽 1시 이전이면 전날로 간주 (예: 3/11 0:30 = 3/10 하키일)
const getHockeyDate = (d) => {
  const dt = d instanceof Date ? d : (d?.toDate ? d.toDate() : new Date(d));
  const adj = new Date(dt);
  if (adj.getHours() < 1) adj.setDate(adj.getDate() - 1);
  return { y: adj.getFullYear(), m: adj.getMonth(), d: adj.getDate() };
};

const getHockeyToday = () => getHockeyDate(new Date());

export const isPast = (date, endDate) => {
  if (!date) return false;
  // endDate가 있으면 endDate 기준, 없으면 date 기준
  const target = endDate || date;
  const d = target.toDate ? target.toDate() : new Date(target);
  // 종료 후 1시간 여유 (새벽 훈련 고려)
  const cutoff = new Date(d);
  cutoff.setHours(cutoff.getHours() + 1);
  return new Date() > cutoff;
};

export const isToday = (date, endDate) => {
  if (!date) return false;
  const today = getHockeyToday();
  // 시작일 또는 종료일이 오늘 하키날짜와 같으면 "오늘"
  const startHD = getHockeyDate(date);
  if (startHD.y === today.y && startHD.m === today.m && startHD.d === today.d) return true;
  if (endDate) {
    const endHD = getHockeyDate(endDate);
    if (endHD.y === today.y && endHD.m === today.m && endHD.d === today.d) return true;
  }
  return false;
};
